// reveal.plugin.js
//
// Vue-директива `v-reveal` — fade-in/stagger/word-reveal анимации появления,
// без GSAP.
//
//   v-reveal                                     — обычный fade-in
//   v-reveal="{ type: 'fade' }"                   — то же явно
//   v-reveal="{ type: 'stagger', stagger: 80 }"   — контейнер: непосредственные
//                                                    дети появляются по очереди
//   v-reveal="{ type: 'words', stagger: 30 }"     — статичный текстовый узел:
//                                                    появляется по словам
//   v-reveal="{ type: 'line' }"                   — divider: ширина 0 → 100%
//   v-reveal="{ immediate: true }"                — старт на первом paint страницы
//
// Опции: stagger (ms), delay (ms, база перед стартом), once (bool, default true),
// threshold, rootMargin — пробрасываются в IntersectionObserver.
//
// ДВА ТРИГГЕРА, ОДНА ДИРЕКТИВА
// ---------------------------
// По умолчанию появление ведёт IntersectionObserver — элемент ждёт скролла.
// `immediate: true` — анимация целиком на CSS-keyframes: стартует на первом
// paint, JS для неё не нужен вообще. Это режим для контента НАД СГИБОМ: он
// анимируется до гидратации и переживает несобравшийся бандл, поэтому LCP стоит
// длительность самой анимации, а не ожидание JS.
//
// НАЧАЛЬНОЕ СОСТОЯНИЕ РЕНДЕРИТ СЕРВЕР
// -----------------------------------
// getSSRProps() отдаёт класс и css-переменные прямо в серверный HTML, а
// applyInitialState() ставит ровно их же на клиенте. Поэтому после гидратации
// менять нечего: элемент уже в том состоянии, из которого начинается анимация.
// Раньше состояние ставилось инлайном в mounted() — сервер отдавал элемент
// видимым, клиент прятал его после гидратации и показывал заново. Отсюда были и
// мигание контента, и провал LCP (браузер считал его по второму paint).
//
// Само СКРЫТОЕ состояние — в ../styles/reveal.scss, не инлайном: инлайн нельзя
// отдать с сервера через getSSRProps для детей stagger-контейнера. Скрытое
// состояние живёт под гейтом `html[data-reveal]`, который ставит блокирующий
// скрипт в <head> (builder/templates/page.js): без JS страница видима целиком, а
// если бандл не собрался — сторож там же снимает гейт через 3 с.
//
// ПОКАЗ — инлайн-стилями (el.style.*), а не классом: любой целевой элемент
// дизайн-системы/темы проекта (.card, .button, .feature-card…) почти наверняка
// уже объявляет свой `transition` — по правилам каскада (@layer / unlayered-тема)
// он перебивает transition-delay из класса, стаггер молча обнуляется. Значения
// длительности и easing инлайн НЕ дублируются: берутся из --reveal-* тех же
// стилей, один источник правды на оба режима.
//
// type:'words' мутирует innerHTML (оборачивает слова в span) — применять только
// к статичным текстовым узлам, не к элементам с реактивным контентом внутри.
// Разбивка идёт после гидратации, но под уже скрытым элементом: переразбивки
// строк не видно.

// Общий registry IntersectionObserver — в utils/intersect.js (его же использует
// v-dock), один observer на комбинацию threshold+rootMargin.
import { observeEl, unobserveEl, prefersReducedMotion } from '../utils/intersect.js';

const STAGGER_DEFAULTS = { fade: 80, stagger: 80, words: 30, line: 0 };

// У этих типов анимируются дети, а не только сам элемент.
const CHILD_TYPES = ['stagger', 'words'];

function isMobileViewport() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches;
}

function normalizeOptions(raw) {
  const o = (raw && typeof raw === 'object') ? raw : {};
  const type = o.type || 'fade';

  return {
    type,
    immediate: !!o.immediate,
    stagger: o.stagger != null ? o.stagger : (STAGGER_DEFAULTS[type] ?? 80),
    delay: o.delay || 0,
    once: o.once !== false,
    threshold: o.threshold != null ? o.threshold : 0.1,
    rootMargin: o.rootMargin || '0px 0px -10% 0px',
  };
}

// Единственное описание начального состояния — отсюда его берут и сервер
// (getSSRProps), и клиент (applyInitialState).
function initialState(opts) {
  const style = { '--reveal-delay': `${opts.delay}ms` };

  if (CHILD_TYPES.includes(opts.type)) {
    style['--reveal-stagger'] = `${opts.stagger}ms`;
  }

  return {
    class: opts.immediate ? 'reveal-in' : 'reveal-init',
    'data-reveal-type': opts.type,
    style,
  };
}

// Клиентский рендер (SPA-переход, ветка v-if) серверного HTML не имеет — состояние
// ставится здесь. На SSR-элементе те же класс и переменные уже стоят, вызов
// идемпотентен.
function applyInitialState(el, opts) {
  const state = initialState(opts);

  el.classList.add(state.class);
  el.setAttribute('data-reveal-type', state['data-reveal-type']);
  Object.entries(state.style).forEach(([name, value]) => el.style.setProperty(name, value));
}

function revealOne(el, opts, delayMs) {
  setTimeout(() => {
    requestAnimationFrame(() => {
      if (opts.type === 'line') {
        el.style.transition = 'width var(--reveal-duration) var(--reveal-ease-line), opacity var(--reveal-duration) var(--reveal-ease-line)';
        el.style.opacity = '1';
        el.style.width = '100%';
      } else {
        el.style.transition = 'opacity var(--reveal-duration) var(--reveal-ease), transform var(--reveal-duration) var(--reveal-ease)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }
      el.classList.add('is-visible');
    });
  }, delayMs);
}

function hideOne(el, opts) {
  el.classList.remove('is-visible');
  if (opts.type === 'line') {
    el.style.opacity = '0';
    el.style.width = '0';
  } else {
    el.style.opacity = '0';
    el.style.transform = 'translateY(50px)';
  }
}

function prepareWordSplit(el) {
  if (el.dataset.revealSplit) return;

  const words = el.textContent.trim().split(/\s+/);
  el.innerHTML = words
    .map((word) => `<span class="reveal-word">${word}</span>`)
    .join(' ');
  el.dataset.revealSplit = 'true';
}

function triggerReveal(el, opts) {
  const scale = isMobileViewport() ? 0.6 : 1;

  revealOne(el, opts, opts.delay);

  if (CHILD_TYPES.includes(opts.type)) {
    Array.from(el.children).forEach((child, i) => {
      revealOne(child, opts, opts.delay + i * opts.stagger * scale);
    });
  }
}

function untriggerReveal(el, opts) {
  hideOne(el, opts);

  if (CHILD_TYPES.includes(opts.type)) {
    Array.from(el.children).forEach((child) => hideOne(child, opts));
  }
}

const revealDirective = {
  // Серверный рендер начального состояния. Класс и переменные попадают прямо в
  // HTML, поэтому гидратация ничего не меняет и второго paint не происходит.
  getSSRProps(binding) {
    return initialState(normalizeOptions(binding.value));
  },

  // Гидратация. Vue 3.5 сверяет class из vnode с class в DOM и ругается
  // mismatch'ем на каждый элемент: класс reveal-init/reveal-in пришёл от
  // getSSRProps, во vnode его нет. Наличие хука created отключает эту сверку
  // (runtime-core: propHasMismatch пропускается, если у элемента есть директива
  // с created) и одновременно ставит то же состояние на клиентском рендере до
  // патча пропсов. Состояние тут и в getSSRProps — одно, из initialState().
  created(el, binding) {
    if (typeof window === 'undefined') return;

    applyInitialState(el, normalizeOptions(binding.value));
  },

  mounted(el, binding) {
    if (typeof window === 'undefined') return;

    const opts = normalizeOptions(binding.value);

    if (prefersReducedMotion()) return; // элемент остаётся как есть, без анимации

    // Повторно: при клиентском рендере патч class-пропа после created затирает
    // выставленный класс. Вызов идемпотентен.
    applyInitialState(el, opts);

    // immediate: анимацию ведут CSS-keyframes, JS в ней не участвует.
    if (opts.immediate) {
      if (opts.type === 'words') {
        console.warn('[v-reveal] immediate + words: разбивка по словам возможна только после гидратации, элемент анимируется целиком.');
      }
      return;
    }

    if (opts.type === 'words') prepareWordSplit(el);

    observeEl(el, {
      once: opts.once,
      show: () => triggerReveal(el, opts),
      hide: () => untriggerReveal(el, opts),
    }, opts);
  },

  unmounted(el) {
    if (typeof window === 'undefined') return;
    unobserveEl(el);
  },
};

export default {
  install(app) {
    app.directive('reveal', revealDirective);

    // Сигнал сторожу в <head> (builder/templates/page.js): директива жива, гейт
    // html[data-reveal] снимать не нужно. Сторож нужен на случай несобравшегося
    // бандла — иначе скрытый с сервера контент остался бы невидимым навсегда.
    if (typeof window !== 'undefined') window.__revealAlive = true;
  },
};

export { revealDirective };
